import React, { useMemo } from 'react';
import { Alert, Button, Col, Form, Input, InputNumber, Row } from 'antd';
import DrawerForm from 'components/DrawerForm';
import { PlusOutlined } from '@ant-design/icons';
import { gql } from '@apollo/client';
import { useMutation } from 'hooks/apollo';

import FormLabel from 'components/FormLabel';
import messages from '../messages';
import HsrRule from './HsrRule';

const CREATE_HEATMAP = gql`
  mutation($websiteId: ID!, $input: MatomoHeatmapInput!) {
    createMatomoHeatmap(websiteId: $websiteId, input: $input)
  }
`;

const UPDATE_HEATMAP = gql`
  mutation($websiteId: ID!, $matomoHsrId: Int!, $input: MatomoHeatmapInput!) {
    updateMatomoHeatmap(websiteId: $websiteId, matomoHsrId: $matomoHsrId, input: $input)
  }
`;

const REMOVE_HEATMAP = gql`
  mutation($websiteId: ID!, $matomoHsrId: Int!) {
    deleteMatomoHeatmap(websiteId: $websiteId, matomoHsrId: $matomoHsrId)
  }
`;

const defaultValues = {
  sampleLimit: 1000,
  sampleRate: 10,
  rules: [{ attribute: 'url', type: 'equals_simple', inverted: false }],
  breakpointMobile: 600,
  breakpointTablet: 960,
};

function getInitialValues(heatmap) {
  return {
    ...defaultValues,
    ...heatmap,
    rules: (heatmap.rules || defaultValues.rules)?.map(rule => ({
      ...rule,
      type: `${rule.inverted ? 'not ' : ''}${rule.type}`,
    })),
  };
}

function getInputValues(values) {
  return {
    ...values,
    rules: values.rules.map(rule => ({
      ...rule,
      type: rule.type.indexOf('not ') === 0 ? rule.type.substring(4) : rule.type,
      inverted: rule.type.indexOf('not ') === 0,
    })),
  };
}

const EditHeatmap = ({ heatmap, open, onCancel, onDone, websiteId }) => {
  const [createHeatmap] = useMutation(CREATE_HEATMAP);
  const [updateWebsite] = useMutation(UPDATE_HEATMAP);
  const [removeHeatmap] = useMutation(REMOVE_HEATMAP);

  const onSubmit = (values) => {
    const input = getInputValues(values);
    if (heatmap?.id) {
      return updateWebsite({ variables: { websiteId, matomoHsrId: heatmap.matomoHsrId, input } });
    }
    return createHeatmap({ variables: { websiteId, input } });
  };

  const onRemove = async () => {
    await removeHeatmap({ variables: { websiteId, matomoHsrId: heatmap.matomoHsrId } });
    onDone();
  };

  const initialValues = useMemo(() => getInitialValues(heatmap), [heatmap]);

  return (
    <DrawerForm
      name="edit-heatmap"
      open={open}
      onSubmit={onSubmit}
      onRemove={heatmap?.id && onRemove}
      onCancel={onCancel}
      onDone={onDone}
      title="Edit Heatmap"
      initialValues={initialValues}
      formReset={heatmap}
    >
      <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Required' }, { max: 50, message: 'Max 50 characters' }]}>
        <Input placeholder="Name" />
      </Form.Item>
      <Form.Item label={<FormLabel label={messages.targetPageRules} description={messages.targetPageRulesDescription} />}>
        <Form.List name="rules">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <HsrRule key={field.key} field={field} remove={remove} />
              ))}
              <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                Add rule
              </Button>
            </>
          )}
        </Form.List>
      </Form.Item>

      <Form.Item name="excludedElements" label={<FormLabel label={messages.excludedElements} description={messages.excludedElementsDescription} />}>
        <Input.TextArea autoSize={{ minRows: 2, maxRows: 10 }} />
      </Form.Item>
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Form.Item name="sampleRate" label={<FormLabel label={messages.sampleRate} description={messages.sampleRateDescription} />} rules={[{ required: true, message: 'Required' }]}>
            <InputNumber min={0} max={100} addonAfter="%" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="breakpointMobile" label={<FormLabel label={messages.breakpointMobile} description={messages.breakpointDescription} />} rules={[{ required: true }]}>
            <InputNumber min={0} addonAfter="px" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="breakpointTablet" label={<FormLabel label={messages.breakpointTablet} description={messages.breakpointDescription} />} rules={[{ required: true }]}>
            <InputNumber min={0} addonAfter="px" />
          </Form.Item>
        </Col>
      </Row>
      <Alert
        message={(
          <>
            Please note a Heatmap may record personal or sensitive information. If you wish to hide certain content on your website or app, you may tag this content by adding a
            {' '}
            <b>data-matomo-mask</b>
            {' '}
            attribute to the HTML markup.
            <br />
            <a href="https://academy.extellio.com/knowledge-base/heatmaps-session-recordings/how-to-mask-elements" target="_blank">Learn more</a>
          </>
        )}
        type="info"
        showIcon
      />
    </DrawerForm>
  );
};

export default EditHeatmap;
